import { forEach, get, size } from 'lodash';
import { getTernaryCondition } from 'common-ui-components/lib/core/utils/common';
import {
  SET_GLOBAL_ERROR,
  LOAD_SHOPPING_CART_DATA_SUCCESS,
  GET_ORDER_SUMMARY_DATA_SUCCESS,
  PLACE_ORDER_SUCCESS,
  QUOTE_ORDER_SUCCESS,
  SEARCH_BAR_SUBMIT,
  LOAD_EMPTY_CART,
  PRODUCT_RECOMMENDATION,
  PRODUCT_RECOMMENDATION_TITLE,
  TRIGGER_ORDER_REVIEW_EVENT,
} from '../constants';
import {
  REMOVE_CART_SUCCESS,
  REMOVE_CART_ITEM_SUCCESS,
  ADD_ITEM_TO_CART_FAILURE,
  ADD_ITEM_TO_CART_SUCCESS,
  UPDATE_CART_SUCCESS,
  BULK_ADD_ITEM_TO_CART_SUCCESS,
  DOWNLOAD_CSV_SUCCESS,
  SHOW_BLOCKED_ACCOUNT_MODAL,
  REQUEST_QUOTE,
  REQUEST_QUOTE_SUCCESS,
  REQUISITION_SUBMIT_SUCCESS,
  SHARE_CART_MODAL_ANALYTICS,
  SHARE_CART_SUBMIT_SUCCESS,
  SHARE_CART_SUBMIT,
} from '../../containers/templates/ShoppingCart/ShoppingCart.constants';
import {
  LOAD_CHECKOUT_DATA_SUCCESS,
  LOAD_SOLD_TO_DETAILS_SUCCESS,
  ADD_ADDRESS,
  PLACE_ORDER,
  REVIEW_ORDER,
  ADD_CREDITCARD_SUCCESS,
  CART_TO_ORDER_ANALYTICS,
  QUOTE_TO_ORDER_ANALYTICS,
  CONTINUE_TO_PAYMENT,
} from '../../containers/templates/Checkout/Checkout.constants';
import {
  CREATE_QUOTE_SUCCESS,
  QUOTE_ANALYTICS_ON_LOAD,
} from '../../containers/templates/Quote/Quote.constants';
import {
  cartContext,
  storeToAnalyticsEventMap,
  SHIPPING_SECTION,
  REVIEW_SECTION,
  PAYMENT_SECTION,
} from './trackingActions';
import { getCartKey, getUserId } from '../../utils/utils';
import { getProductRecommendationAnalyticsData, getOrCondition } from '../../utils/utilFunctions';
import PAYMENT_CONSTANTS from '../../components/templates/Checkout/PaymentContainer/PaymentSection/Payment/Payment.constants';

const { PAYMENT_METHOD } = PAYMENT_CONSTANTS;

const getItemData = data => ({
  productCategory: '',
  SKU: get(data, ['product', 'code'], ''),
  productName: get(data, ['product', 'name'], ''),
  quantity: get(data, ['quantity'], 0),
  price: get(data, ['customerPrice', 'value'], '') || get(data, ['basePrice', 'value'], ''),
  stockMessage: get(data, ['product', 'purchasable'], false),
});

export const wrapCartData = (data = {}) => ({ cart: data });

export const wrapErrorData = (data = {}) => ({ error: data });

export const wrapCheckoutData = (data = {}) => ({ checkout: data });

export const wrapOrderData = (data = {}) => ({ order: data });

export const wrapSearchData = (data = {}) => ({ search: data });

export const wrapQuoteData = (data = {}) => ({ quote: data });

export const wrapShareCartData = (data = {}) => ({ shareCart: data });

export const wrapSoldToDetailsData = (data = {}) => ({ soldToDetails: data });

export const wrapProductRecommendationData = (
  productDetails = {},
  eventDetails = {},
  eventName = ''
) => ({
  productDetails,
  eventDetails,
  eventName,
});

const transformPageData = (data, type, initialConfigs) => {
  const config = initialConfigs;
  switch (type) {
    case LOAD_CHECKOUT_DATA_SUCCESS:
      config.page = { ...config.page, ...{ name: SHIPPING_SECTION } };
      return config;
    case ADD_ADDRESS:
      config.page = {
        ...config.page,
        ...{ name: data.addressType === 'SHIPPING' ? SHIPPING_SECTION : PAYMENT_SECTION },
      };
      return config;
    case PLACE_ORDER:
      config.page = {
        ...config.page,
        ...{ name: data.orderSimulate ? SHIPPING_SECTION : REVIEW_SECTION },
      };
      return config;
    case REVIEW_ORDER:
      config.page = {
        ...config.page,
        ...{ name: PAYMENT_SECTION },
      };
      return config;
    default:
      return config;
  }
};

const getEventName = (data, type) => {
  switch (type) {
    case ADD_ADDRESS:
      return storeToAnalyticsEventMap[`${type}_${data.addressType}`];
    case PLACE_ORDER:
      return storeToAnalyticsEventMap[`${type}_${data.orderSimulate ? 'SHIPPING' : 'REVIEW'}`];
    case QUOTE_ORDER_SUCCESS:
    case PLACE_ORDER_SUCCESS:
      return storeToAnalyticsEventMap[`${type}${data.orderSimulate ? '_SIMULATE' : ''}`];
    case GET_ORDER_SUMMARY_DATA_SUCCESS:
      return storeToAnalyticsEventMap[
        `${type}_${get(data, ['requestData', 'remove'], false) ? 'REMOVE' : 'ADD'}`
      ];
    default:
      return storeToAnalyticsEventMap[type];
  }
};
const getData = data => data?.entries?.map(product => getItemData(product));
// eslint-disable-next-line complexity
const transformTrackingData = (data, type) => {
  const cartID = getCartKey();
  switch (type) {
    /* Checkout Events -- Start */
    case CONTINUE_TO_PAYMENT:
    case LOAD_CHECKOUT_DATA_SUCCESS:
    case ADD_ADDRESS:
    case REVIEW_ORDER:
    case PLACE_ORDER:
      return wrapCheckoutData();
    case QUOTE_ORDER_SUCCESS:
    case PLACE_ORDER_SUCCESS: {
      const {
        shipTo,
        billTo,
        poNumber,
        cardDetails,
        quoteId,
        orderSimulate = false,
        ...orderParams
      } = data;
      const paymentMethod = getTernaryCondition(
        poNumber,
        PAYMENT_METHOD.PURCHASE_ORDER,
        PAYMENT_METHOD.CREDIT_CARD
      );
      return orderSimulate
        ? wrapCheckoutData({ cartID, quoteId })
        : wrapOrderData({ ...orderParams, paymentMethod, cartID, poNumber, quoteId });
    }
    case CREATE_QUOTE_SUCCESS:
      return wrapQuoteData({ ...data, cartID });

    /* Checkout Events -- End */

    /* Shopping Cart Events -- Start */
    case LOAD_SHOPPING_CART_DATA_SUCCESS: {
      const item = getData(data);
      const { totalItems = 0 } = data;
      return wrapCartData({
        cartID,
        currencyCode: get(data, ['totalPrice', 'currencyIso'], ''),
        cartTotal: get(data, ['totalPrice', 'value'], ''),
        item: getOrCondition(item, []),
        isCartEmpty: totalItems === 0,
      });
    }
    case REMOVE_CART_SUCCESS: {
      const item = getData(data);
      return wrapCartData({
        cartID,
        currencyCode: get(data, ['totalPrice', 'currencyIso'], ''),
        item: getOrCondition(item, []),
      });
    }
    case ADD_ITEM_TO_CART_SUCCESS: {
      const pageName = window.location.pathname.split('/').pop();
      const SKU = get(data, 'productSKU', '');
      const quantity = get(data, 'quantity', '');
      const { isProductRecommendation = false } = data;
      let addToCartContext = cartContext[pageName];
      if (isProductRecommendation) {
        const prAnalyticsData = getProductRecommendationAnalyticsData(data, pageName);
        addToCartContext = get(prAnalyticsData, 'cartContext', '');
      }
      return wrapCartData({
        cartID,
        cartContext: addToCartContext,
        item: [
          {
            SKU,
            quantity,
          },
        ],
      });
    }
    case UPDATE_CART_SUCCESS: {
      const item = data.itemUpdated && [getItemData(data.itemUpdated)];
      return wrapCartData({
        cartID,
        currencyCode: get(data, ['itemUpdated', 'basePrice', 'currencyIso'], ''),
        item: getOrCondition(item, []),
      });
    }
    case REMOVE_CART_ITEM_SUCCESS: {
      const item = data.itemRemoved && [getItemData(data.itemRemoved)];
      return wrapCartData({
        cartID,
        currencyCode: get(data, ['itemRemoved', 'basePrice', 'currencyIso'], ''),
        item: getOrCondition(item, []),
      });
    }
    case ADD_ITEM_TO_CART_FAILURE:
      return wrapCartData({
        cartID,
        SKU: get(data, 'invalidSKU', ''),
      });
    case SHOW_BLOCKED_ACCOUNT_MODAL:
      return wrapCartData({
        cartID,
        accountId: getUserId(),
      });
    case REQUISITION_SUBMIT_SUCCESS:
      return wrapCartData({
        ...get(data, 'cart', {}),
      });
    /* Shopping Cart Events -- End */

    /* OrderSummary Events -- Start */
    case GET_ORDER_SUMMARY_DATA_SUCCESS:
      return wrapCartData({
        promotionCode: get(data, ['requestData', 'body', 'coupons', '0', 'code'], ''),
      });
    /* OrderSummary Events -- End */

    /* QuickOrder Events -- Start */
    case BULK_ADD_ITEM_TO_CART_SUCCESS: {
      const item = data?.products?.map(product => ({
        SKU: get(product, ['entry', 'product', 'code'], ''),
        quantity: getOrCondition(product.quantity, ''),
      }));
      return wrapCartData({
        cartID,
        cartContext: cartContext.bulkUploadQuickOrder || '',
        item: getOrCondition(item, []),
      });
    }
    /* QuickOrder Events -- End */

    /* Request a Quote, Download CSV */
    case DOWNLOAD_CSV_SUCCESS:
    case REQUEST_QUOTE:
    case ADD_CREDITCARD_SUCCESS:
      return wrapCartData({
        cartID,
      });
    case REQUEST_QUOTE_SUCCESS:
      return wrapCartData({
        cartID,
        ...data,
      });
    /* Request a Quote end */

    case TRIGGER_ORDER_REVIEW_EVENT: {
      const { quoteId } = data;
      return wrapCheckoutData({ cartID, quoteId });
    }
    /* Search Events */
    case SEARCH_BAR_SUBMIT:
      return wrapSearchData(data.searchTerm);
    /* Search Events end */

    /* Global Error Events */
    case SET_GLOBAL_ERROR:
      return wrapErrorData(data);

    case SHARE_CART_SUBMIT_SUCCESS:
    case SHARE_CART_SUBMIT:
    case SHARE_CART_MODAL_ANALYTICS:
      if (type === SHARE_CART_SUBMIT) {
        const { message, products } = data;
        return wrapSearchData({ cartID, message, products });
      }
      return wrapSearchData({ cartID, ...data });
    case QUOTE_ANALYTICS_ON_LOAD:
    case CART_TO_ORDER_ANALYTICS:
    case QUOTE_TO_ORDER_ANALYTICS:
      return wrapCartData(data);
    case LOAD_SOLD_TO_DETAILS_SUCCESS:
      return wrapSoldToDetailsData({ customerNumber: get(data, 'customerNumber') });
    case LOAD_EMPTY_CART:
      return wrapCartData({ ...data, isCartEmpty: true });
    case PRODUCT_RECOMMENDATION_TITLE:
    case PRODUCT_RECOMMENDATION: {
      // eslint-disable-next-line no-console
      console.log('type = ', type, 'data = ', data);
      const productDetails = [];
      let eventDetails = {};
      if (size(data) > 0) {
        forEach(data, item => {
          productDetails.push({
            productSKU: get(item, ['id'], ''),
            productID: get(item, ['productID'], ''),
            productName: get(item, ['name'], ''),
          });
        });
        const eventData = get(data, ['0', 'activityDetails1'], {});
        eventDetails = {
          at_rec_criteria: get(eventData, ['criteria.title'], ''),
          at_activity: get(eventData, ['campaign.name'], ''),
          at_activity_experience: get(eventData, ['campaign.recipe.name'], ''),
        };
      }
      const eventName = storeToAnalyticsEventMap[type];
      return wrapProductRecommendationData(productDetails, eventDetails, eventName);
    }
    default:
      return wrapCartData(data);
  }
};

export default transformTrackingData;
export { getEventName, transformPageData };
